import { memo, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Ban, Eye, Film } from "lucide-react";
import type { LibraryItem } from "@/api/models/api-types";
import { AssetTile, AssetTileCheckbox } from "@/features/library/components/AssetTile";
import { useCanHover } from "@/features/library/hooks/use-can-hover";
import { cn } from "@/lib/utils";
import { getPickCompatibility, toSelectedFile } from "../lib/pick-compatibility";
import { useFilePickerStore } from "../store/file-picker-store";

const LONG_PRESS_MS = 450;
const DEFAULT_MAX_SELECTION = 10;

interface PickerAssetCardProps {
  item: LibraryItem;
}

/**
 * Seçicideki kart. Tıklama seçer; göz, Space ya da uzun basma hızlı önizlemeyi açar.
 * Uygun olmayan türler soluk görünür ve nedenini söyler; görsel istenen alanda
 * videolar kare seçmek için önizlemeye yönlenir.
 */
export const PickerAssetCard = memo(({ item }: PickerAssetCardProps) => {
  const { t } = useTranslation();
  const canHover = useCanHover();
  const config = useFilePickerStore((state) => state.config);
  const isSelected = useFilePickerStore((state) =>
    state.selectedFiles.some((file) => file.id === item._id)
  );
  const isFull = useFilePickerStore(
    (state) => state.selectedFiles.length >= (state.config?.maxSelection || DEFAULT_MAX_SELECTION)
  );
  const selectFile = useFilePickerStore((state) => state.selectFile);
  const removeFile = useFilePickerStore((state) => state.removeFile);
  const setQuickLookId = useFilePickerStore((state) => state.setQuickLookId);
  const [isReasonShown, setIsReasonShown] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const didLongPressRef = useRef(false);

  const compatibility = useMemo(() => getPickCompatibility(item, config), [item, config]);
  const isMulti = (config?.maxSelection || DEFAULT_MAX_SELECTION) > 1;
  const isBlocked = compatibility.status === "blocked";
  const isFrameOnly = compatibility.status === "frame";
  const isLimitReached = isMulti && isFull && !isSelected;

  const openQuickLook = () => setQuickLookId(item._id);

  const toggle = () => {
    if (isFrameOnly) {
      openQuickLook();
      return;
    }
    if (isBlocked) {
      setIsReasonShown((shown) => !shown);
      return;
    }
    if (isSelected) removeFile(item._id);
    else if (!isLimitReached) selectFile(toSelectedFile(item), item);
  };

  const clearLongPress = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    didLongPressRef.current = false;
    if (event.pointerType === "mouse") return;
    clearLongPress();
    longPressTimerRef.current = window.setTimeout(() => {
      didLongPressRef.current = true;
      openQuickLook();
    }, LONG_PRESS_MS);
  };

  const handleClick = () => {
    if (didLongPressRef.current) {
      didLongPressRef.current = false;
      return;
    }
    toggle();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === " ") {
      event.preventDefault();
      openQuickLook();
    }
  };

  const title = item.title || t("filePicker.untitled");

  return (
    <AssetTile item={item} isSelected={isSelected} isDisabled={isBlocked || isLimitReached}>
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerUp={clearLongPress}
        onPointerLeave={clearLongPress}
        onPointerCancel={clearLongPress}
        onContextMenu={(event) => !canHover && event.preventDefault()}
        aria-pressed={isSelected}
        aria-disabled={isBlocked || isLimitReached}
        aria-label={title}
        title={isBlocked && compatibility.status === "blocked" ? t(compatibility.reasonKey) : title}
        className={cn(
          "absolute inset-0 z-10 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-500 select-none [-webkit-touch-callout:none]",
          isBlocked || isLimitReached ? "cursor-not-allowed" : "cursor-pointer"
        )}
      />

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-14 bg-gradient-to-b from-black/40 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

      {!isBlocked && !isFrameOnly && isMulti && (
        <AssetTileCheckbox
          isSelected={isSelected}
          isAlwaysVisible={isSelected}
          onToggle={toggle}
          disabled={isLimitReached}
          label={t("library.actions.select")}
        />
      )}

      <button
        type="button"
        onClick={openQuickLook}
        aria-label={t("filePicker.quickLook.open")}
        title={t("filePicker.quickLook.open")}
        className={cn(
          "absolute top-2 right-2 z-20 w-8 h-8 rounded-lg flex items-center justify-center bg-black/35 text-white backdrop-blur-md transition-all duration-200 hover:bg-black/55",
          canHover ? "opacity-0 group-hover:opacity-100 focus-visible:opacity-100" : "opacity-100"
        )}
      >
        <Eye className="w-4 h-4" />
      </button>

      {isFrameOnly && (
        <span className="pointer-events-none absolute bottom-2 left-2 z-20 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 px-2 py-1 text-[11px] font-semibold text-white shadow-lg">
          <Film className="w-3 h-3" />
          {t("filePicker.frame.badge")}
        </span>
      )}

      {compatibility.status === "blocked" && (
        <span
          className={cn(
            "pointer-events-none absolute inset-x-2 bottom-2 z-20 flex items-center gap-1.5 rounded-lg bg-gray-900/85 dark:bg-black/80 px-2 py-1.5 text-[11px] font-medium leading-tight text-white backdrop-blur-md transition-opacity duration-200",
            isReasonShown ? "opacity-100" : canHover ? "opacity-0 group-hover:opacity-100" : "opacity-0"
          )}
        >
          <Ban className="w-3 h-3 shrink-0" />
          <span className="line-clamp-2">{t(compatibility.reasonKey)}</span>
        </span>
      )}
    </AssetTile>
  );
});

PickerAssetCard.displayName = "PickerAssetCard";
